import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { MetricsBar } from '../components/MetricsBar';
import { Award, CheckCircle2, ChevronRight, Activity } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function Scorecard() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentScores, setRecentScores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedScore, setSelectedScore] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!token) return;
      try {
        setLoading(true);
        const apiUrl = process.env.REACT_APP_BACKEND_URL;
        
        // 1. Fetch Stats
        const statsRes = await axios.get(`${apiUrl}/api/v1/clinicalsim/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStats(statsRes.data);

        // 2. Fetch Sessions
        const sessionsRes = await axios.get(`${apiUrl}/api/v1/clinicalsim/sessions`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const allSessions = sessionsRes.data?.sessions || [];
        const completed = allSessions.filter(s => s.status === 'completed').slice(0, 5);

        // 3. Fetch Scores & Cases for these completed sessions
        const scoresWithData = await Promise.all(completed.map(async (session) => {
          try {
            const [scoreRes, caseRes] = await Promise.all([
              axios.get(`${apiUrl}/api/v1/clinicalsim/sessions/${session.session_id}/score`, { headers: { Authorization: `Bearer ${token}` } }),
              axios.get(`${apiUrl}/api/v1/clinicalsim/cases/${session.case_id}`, { headers: { Authorization: `Bearer ${token}` } })
            ]);
            return {
              session,
              caseInfo: caseRes.data,
              score: scoreRes.data
            };
          } catch (err) {
            return null; // Score might not be available yet
          }
        }));

        setRecentScores(scoresWithData.filter(Boolean));
      } catch (err) {
        console.error('Failed to load scorecard data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500" />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-800 tracking-tight">Performance Scorecard</h1>
        <p className="text-slate-500 mt-1">Overview of your clinical simulation performance.</p>
      </div>

      <MetricsBar stats={stats} />

      <div className="mt-12">
        <h2 className="text-xl font-bold text-slate-800 mb-6 flex items-center gap-2">
          <Award className="w-6 h-6 text-violet-500" />
          Recent Evaluations
        </h2>

        {recentScores.length === 0 ? (
          <div className="bg-white rounded-3xl border border-slate-200 p-12 text-center shadow-sm">
            <Activity className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-slate-700 mb-2">No evaluations yet</h3>
            <p className="text-slate-500">Complete a simulation case to see your detailed performance breakdown here.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {recentScores.map((item, idx) => (
              <div key={idx} className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 font-bold text-sm">
                        {item.caseInfo.patient?.initials || 'PT'}
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-slate-800">{item.caseInfo.title}</h3>
                        <p className="text-sm text-slate-500">{new Date(item.session.created_at).toLocaleDateString()} • Attempt #{item.session.attempt_number}</p>
                      </div>
                    </div>
                    {item.score.summary && (
                      <p className="text-sm text-slate-600 italic line-clamp-2 mt-3 pl-14 border-l-2 border-slate-200 ml-[18px]">
                        "{item.score.summary}"
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-8 md:border-l border-slate-100 md:pl-8">
                    <div className="text-center">
                      <div className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Score</div>
                      <div className="text-3xl font-black text-violet-600">{Math.round(item.score.total_score)}%</div>
                    </div>
                    
                    <button 
                      onClick={() => setSelectedScore(item)}
                      className="flex items-center justify-center w-10 h-10 rounded-full bg-slate-50 hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>

                </div>

                {/* Breakdown Mini-bars */}
                <div className="mt-6 pt-5 border-t border-slate-100 grid grid-cols-2 md:grid-cols-6 gap-4">
                  {Object.entries(item.score.breakdown || {}).map(([key, val]) => (
                    <div key={key}>
                      <div className="flex justify-between items-end mb-1.5">
                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider truncate mr-2">
                          {key.replace('_', ' ')}
                        </span>
                        <span className="text-[10px] font-bold text-slate-700">{Math.round(val)}%</span>
                      </div>
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-violet-400 rounded-full"
                          style={{ width: `${val}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Evaluation Summary Modal */}
      {selectedScore && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
            onClick={() => setSelectedScore(null)}
          />
          <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
              <div>
                <h2 className="text-xl font-bold text-slate-800">Evaluation Details</h2>
                <p className="text-sm text-slate-500 mt-1">
                  {selectedScore.caseInfo.title} • Attempt #{selectedScore.session.attempt_number}
                </p>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right mr-4">
                  <div className="text-xs font-bold text-slate-400 uppercase tracking-widest">Total Score</div>
                  <div className="text-2xl font-black text-violet-600">{Math.round(selectedScore.score.total_score)}%</div>
                </div>
                <button 
                  onClick={() => setSelectedScore(null)}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-200 text-slate-500 hover:bg-slate-300 transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                </button>
              </div>
            </div>
            
            <div className="p-8 overflow-y-auto">
              {selectedScore.score.summary && (
                <div className="mb-8">
                  <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <Activity className="w-4 h-4 text-violet-500" />
                    Overall Performance
                  </h3>
                  <div className="bg-violet-50 text-violet-900 p-5 rounded-2xl text-sm leading-relaxed border border-violet-100/50">
                    {selectedScore.score.summary}
                  </div>
                </div>
              )}

              {selectedScore.score.feedback && selectedScore.score.feedback.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-4 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    Key Feedback
                  </h3>
                  <ul className="space-y-3">
                    {selectedScore.score.feedback.map((point, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-sm text-slate-600">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 mt-2 shrink-0" />
                        <span className="leading-relaxed">{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {Object.entries(selectedScore.score.breakdown || {}).map(([key, val]) => (
                  <div key={key} className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">
                      {key.replace('_', ' ')}
                    </div>
                    <div className="flex items-end justify-between">
                      <div className="text-lg font-bold text-slate-700">{Math.round(val)}%</div>
                    </div>
                    <div className="h-1.5 bg-slate-200 rounded-full mt-2 overflow-hidden">
                      <div 
                        className="h-full bg-violet-500 rounded-full"
                        style={{ width: `${val}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="p-4 border-t border-slate-100 bg-slate-50 flex justify-end gap-3">
              {selectedScore.score.recording_url && (
                <a 
                  href={selectedScore.score.recording_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="px-5 py-2.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-bold rounded-xl transition-colors"
                >
                  Listen to Recording
                </a>
              )}
              <button 
                onClick={() => setSelectedScore(null)}
                className="px-6 py-2.5 bg-violet-500 hover:bg-violet-600 text-white text-sm font-bold rounded-xl shadow-sm transition-colors"
              >
                Close Summary
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
